import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Icon from '../../../base/icons/components/Icon';
import { IconCheck, IconCloseLarge, IconHighlight } from '../../../base/icons/svg';
import { getParticipantById, getParticipantDisplayName } from '../../../base/participants/functions';
import { updateSettings } from '../../../base/settings/actions';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import ClickableIcon from '../../../base/ui/components/web/ClickableIcon';
import { appendSuffix } from '../../functions';
const useStyles = makeStyles()(theme => {
    return {
        displayNameContainer: {
            overflow: 'visible !important'
        },
        displayName: {
            ...withPixelLineHeight(theme.typography.labelBold),
            color: theme.palette.text01,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
        },
        editDisplayName: {
            outline: 'none',
            border: 'none',
            background: 'none',
            boxShadow: 'none',
            padding: 0,
            ...withPixelLineHeight(theme.typography.labelBold),
            color: theme.palette.text01
        },
        icon: {
            padding: '2px',
            backgroundColor: theme.palette.action03,
            border: 0,
            outline: 0,
            borderRadius: `${theme.shape.borderRadius}px`,
            '&:hover': {
                backgroundColor: theme.palette.ui02
            }
        }
    };
});
const DisplayName = ({ allowEditing, displayNameSuffix, elementID, participantID }) => {
    const { classes } = useStyles();
    const localStatus = useSelector((state) => getParticipantById(state, participantID))?.local ?? '';
    const nameToDisplay = useSelector((state) => getParticipantDisplayName(state, participantID));
    const [editDisplayNameValue, setEditDisplayNameValue] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const nameInputRef = useRef(null);
    useEffect(() => {
        if (isEditing && nameInputRef.current) {
            nameInputRef.current.select();
        }
    }, [isEditing]);
    const onClick = useCallback((e) => {
        e.stopPropagation();
    }, []);
    const onBlur = useCallback((event) => {
        const target = event.relatedTarget;
        // Wenn auf Annehmen oder Abblehnen geklickt wird soll nicht automatisch dismissed werden
        if (target?.id === 'display-name-accept-button' || target?.id === 'display-name-cancel-button') {
            return;
        }
        setIsEditing(false);
    }, []);
    const onChange = useCallback((event) => {
        setEditDisplayNameValue(event.target.value);
    }, []);
    const onSubmit = useCallback(() => {
        // Name leer -> Verwerfen
        if (editDisplayNameValue) {
            dispatch(updateSettings({
                displayName: editDisplayNameValue
            }));
            setEditDisplayNameValue('');
            nameInputRef.current = null;
        }
        setIsEditing(false);
    }, [editDisplayNameValue, nameInputRef]);
    const onDismiss = useCallback(() => {
        setIsEditing(false);
    }, []);
    // Enter speichert Änderungen, Escape verwirft
    const onKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            onSubmit();
        }
        if (event.key === 'Escape') {
            onDismiss();
        }
    }, [onSubmit, onDismiss]);
    const onStartEditing = useCallback((e) => {
        if (allowEditing) {
            e.stopPropagation();
            setIsEditing(true);
            setEditDisplayNameValue('');
        }
    }, [allowEditing]);
    // Screenreader: Enter startet editing
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            onStartEditing(e);
        }
    }, [onStartEditing]);
    if (allowEditing && isEditing) {
        return (React.createElement("div", { onBlur: onBlur, onClick: onClick },
            React.createElement("input", { autoFocus: true, className: classes.editDisplayName, id: 'editDisplayName', onChange: onChange, onKeyDown: onKeyDown, placeholder: t('defaultNickname'), ref: nameInputRef, spellCheck: 'false', type: 'text', value: editDisplayNameValue }),
            React.createElement(ClickableIcon, { accessibilityLabel: t('dialog.accessibilityLabel.SaveName'), icon: IconCheck, id: 'display-name-accept-button', onClick: onSubmit }),
            React.createElement(ClickableIcon, { accessibilityLabel: t('dialog.Cancel'), icon: IconCloseLarge, id: 'display-name-cancel-button', onClick: onDismiss })));
    }
    return (React.createElement("div", { className: `displayNameContainer ${classes.displayNameContainer}` },
        React.createElement("span", { "aria-label": t('dialog.accessibilityLabel.ChangeName', { name: nameToDisplay }), className: `displayname ${classes.displayName}`, id: elementID, onClick: onStartEditing, onKeyDown: handleKeyDown, role: 'button', tabIndex: 0 },
            appendSuffix(nameToDisplay, displayNameSuffix),
            localStatus
                && React.createElement(Icon, { className: classes.icon, id: 'display-name-change-button', size: 24, src: IconHighlight }))));
};
export default DisplayName;
